import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const faqData = [
  { q: 'Pourquoi mon volet roulant ne remonte-t-il plus ?', a: 'Les causes peuvent être multiples : moteur hors service, condensateur défectueux, fin de course déréglé, sangle cassée (volet manuel), ou encore un problème électrique au niveau de l\'interrupteur. Seul un diagnostic sur place permet de déterminer la cause exacte.' },
  { q: 'Pouvez-vous réparer un volet roulant électrique qui ne remonte plus le jour même ?', a: 'Nous faisons notre maximum pour intervenir dans les 24 à 48 heures. Pour les urgences, si notre planning le permet, nous pouvons intervenir le jour même. Appelez-nous pour connaître nos disponibilités à Lyon.' },
];

export default function VoletNeRemontePlus() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({
      '@type': 'Question', name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <>
      <SEOHead
        title="Volet roulant qui ne remonte plus à Lyon | Dépannage DFPRO"
        description="Volet roulant qui ne remonte plus à Lyon ? Diagnostic et réparation rapide : moteur, condensateur, sangle, fin de course. 7j/7, devis gratuit."
        canonicalUrl="https://depanfenetres-lyon.fr/volet-roulant-ne-remonte-plus-lyon/"
        jsonLd={faqJsonLd}
      />
      <Header />
      <section className="w-full">
        <img src="/images/dfpro-volet-ne-remonte-plus-2026.jpg" alt="Réparation d'un volet roulant qui ne remonte plus à Lyon" className="w-full h-[200px] md:h-[300px] object-cover object-center" loading="lazy" />
      </section>


      <main className="pt-20">
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-secondary-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors">Accueil</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <Link to="/reparation-volet-roulant-lyon/" className="hover:text-primary-500 transition-colors">Volet roulant</Link>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-secondary-500">Ne remonte plus</span>
            </nav>
            <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-800">Volet roulant qui ne remonte plus à Lyon</h1>
            <p className="mt-4 text-sm md:text-lg text-secondary-500 max-w-3xl leading-relaxed">
              Votre volet roulant reste bloqué en bas et refuse de remonter ? Nous diagnostiquons et réparons la panne rapidement à Lyon et dans la métropole.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2 space-y-8">
                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Les causes d'un volet roulant qui ne remonte plus</h2>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Un volet qui ne remonte plus est l'une des pannes les plus frustrantes, surtout quand il fait beau dehors et que vous voulez profiter de la lumière du jour. Cette panne peut avoir plusieurs origines selon le type de volet roulant que vous possédez.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Pour un volet roulant électrique, les causes les plus fréquentes sont un moteur qui a rendu l'âme, un condensateur de démarrage défectueux, ou un problème de fin de course. Le condensateur est un petit composant électronique qui donne l'impulsion électrique nécessaire au démarrage du moteur. Quand il est HS, le moteur ne démarre tout simplement pas, même s'il est encore en bon état.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Le fin de course est un dispositif qui indique au moteur quand s'arrêter. S'il est déréglé, le moteur peut croire que le volet est déjà en position haute et refuser de fonctionner. Ce réglage est une opération délicate que nos techniciens maîtrisent parfaitement.
                  </p>
                  <p className="mt-3 text-sm text-secondary-500 leading-relaxed">
                    Pour un volet roulant manuel à sangle, le problème vient généralement de la sangle elle-même (cassée ou sortie de l'enrouleur) ou du mécanisme de treuil. Si la manivelle tourne dans le vide, c'est souvent le cardan ou le réducteur qui est en cause.
                  </p>
                </div>

                <div>
                  <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800">Nos solutions pour chaque type de panne</h2>
                  <div className="mt-4 space-y-4">
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Moteur hors service</h3>
                      <p className="mt-2 text-sm text-secondary-500 leading-relaxed">Remplacement du moteur par un modèle compatible avec votre installation. Nous travaillons avec les moteurs Somfy, Bubendorff, Profalux, Simu et autres grandes marques. L'intervention dure environ 1 à 2 heures, garantie 10 ans.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Condensateur défectueux</h3>
                      <p className="mt-2 text-sm text-secondary-500 leading-relaxed">Remplacement du condensateur uniquement, sans changer le moteur. Une réparation économique (quelques dizaines d'euros) qui peut suffire à résoudre la panne si le moteur est en bon état.</p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-4 md:p-5">
                      <h3 className="font-heading font-semibold text-sm text-foreground-800">Sangle cassée ou usée</h3>
                      <p className="mt-2 text-sm text-secondary-500 leading-relaxed">Remplacement de la sangle et révision du mécanisme d'enroulement. Nous pouvons également vous proposer de passer à une motorisation électrique si vous souhaitez moderniser votre installation.</p>
                    </div>
                  </div>
                </div>
              </div>

              <aside className="space-y-6">
                <div className="bg-background-100 rounded-lg p-5 sticky top-24">
                  <h3 className="font-heading font-semibold text-sm text-foreground-800 mb-4">Services connexes</h3>
                  <nav className="space-y-1">
                    <Link to="/reparation-moteur-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation moteur</Link>
                    <Link to="/changer-sangle-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Changement de sangle</Link>
                    <Link to="/manivelle-volet-roulant-lyon/" className="block text-sm text-secondary-500 hover:text-primary-500 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors">Réparation manivelle</Link>
                    <Link to="/reparation-volet-roulant-lyon/" className="block text-sm text-primary-500 hover:text-primary-600 hover:bg-primary-50 rounded-md px-3 py-2 transition-colors font-medium">← Tous nos services</Link>
                  </nav>
                </div>
              </aside>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-semibold text-lg md:text-xl text-foreground-800 text-center mb-8">Questions fréquentes</h2>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner title="Votre volet ne remonte plus ?" subtitle="Appelez-nous, nous intervenons rapidement à Lyon. Diagnostic gratuit." />
      </main>
      <Footer />
    </>
  );
}